import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Pause, Play, Search } from "lucide-react";
import { Chip, inputCls } from "../components/Chips";
import { Cover } from "../components/Cover";
import { SongCard } from "../components/SongCard";
import { AuthorLink } from "../components/AuthorLink";
import { DiscoverPlaylists } from "../components/DiscoverPlaylists";
import { usePlayer } from "../context/PlayerContext";
import { http } from "../lib/api";
import { GENRES, genreName } from "../lib/presets";

const SORTS = [["trending", "Populiaru dabar"], ["new", "Naujausios"], ["top", "Visų laikų top"]];

const Spotlight = ({ song, list }) => {
  const { current, playing, play } = usePlayer();
  const on = current?.id === song.id && playing;
  return (
    <div data-testid="discover-spotlight" className="relative overflow-hidden rounded-3xl border border-[var(--c-line)] min-h-[280px] flex items-end">
      <Cover song={song} className="absolute inset-0 w-full h-full scale-110 blur-2xl opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--c-bg)] via-[var(--c-bg)]/80 to-transparent" />
      <div className="relative flex flex-col sm:flex-row gap-6 sm:items-end p-6 sm:p-8 w-full">
        <Cover song={song} className="w-40 h-40 rounded-2xl shadow-2xl shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold"><Flame className="w-3.5 h-3.5" />Savaitės hitas</p>
          <Link to={`/daina/${song.id}`} className="block font-display text-3xl sm:text-4xl font-bold mt-2 truncate hover:text-[var(--c-orange)] transition-colors">{song.title}</Link>
          <p className="text-sm text-zinc-400 mt-1"><AuthorLink song={song} testId="spotlight-author-link" /> · {genreName(song.genre) || "Lietuviška daina"} · {song.plays} perklausų</p>
        </div>
        <button data-testid="spotlight-play" onClick={() => play(song, list)}
          className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-semibold hover:bg-[var(--c-orange)] hover:text-white transition-colors duration-200 self-start sm:self-end">
          {on ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4" fill="currentColor" />}{on ? "Pauzė" : "Klausyti"}
        </button>
      </div>
    </div>
  );
};

export default function Discover() {
  const [sort, setSort] = useState("trending");
  const [genre, setGenre] = useState("");
  const [q, setQ] = useState("");
  const [songs, setSongs] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => { http.get("/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => http.get("/discover", { params: { sort, genre, q } }).then((r) => setSongs(r.data)), 250);
    return () => clearTimeout(t);
  }, [sort, genre, q]);

  return (
    <div className="space-y-10">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 rise">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold mb-4">Bendruomenė</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold">Atrasti</h1>
        </div>
        {stats && (
          <div data-testid="discover-stats" className="flex gap-8 font-mono">
            {[["dainų", stats.songs], ["perklausų", stats.plays], ["kūrėjų", stats.authors]].map(([l, v]) => (
              <div key={l}><p className="text-2xl font-display">{v}</p><p className="text-[11px] uppercase tracking-wider text-zinc-500">{l}</p></div>
            ))}
          </div>
        )}
      </header>

      {songs?.[0] && sort === "trending" && !genre && !q && <Spotlight song={songs[0]} list={songs} />}

      {!genre && !q && <DiscoverPlaylists />}

      <div className="flex flex-col xl:flex-row gap-4 xl:items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {SORTS.map(([id, l]) => <Chip key={id} testId={`sort-${id}`} active={sort === id} onClick={() => setSort(id)}>{l}</Chip>)}
        </div>
        <div className="relative xl:w-72">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="discover-search-input" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-10 py-2.5`} placeholder="Ieškoti dainų, autorių..." />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap -mt-4">
        <Chip testId="discover-genre-all" active={!genre} onClick={() => setGenre("")} color="violet">Visi</Chip>
        {GENRES.map((g) => <Chip key={g.id} testId={`discover-genre-${g.id}`} active={genre === g.id} onClick={() => setGenre(g.id)} color="violet">{g.name}</Chip>)}
      </div>

      {songs && songs.length === 0 && (
        <div data-testid="discover-empty" className="text-center py-20 rounded-3xl border border-dashed border-[var(--c-line)]">
          <p className="text-zinc-400">Dainų nerasta. <Link to="/" className="text-[var(--c-orange)] hover:underline">Sukurk pirmąją!</Link></p>
        </div>
      )}
      <div data-testid="discover-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-x-5 gap-y-8">
        {songs?.map((s, i) => <SongCard key={s.id} song={s} list={songs} index={i} />)}
      </div>
    </div>
  );
}
